import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, ChevronUp, ChevronDown, ExternalLink, Eye, EyeOff, Star } from 'lucide-react';
import { adminFetch } from '../../lib/adminApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const input = "w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none";
const label = "block text-xs text-gray-400 mb-1";

const emptyItem = { title: '', title_es: '', subtitle: '', subtitle_es: '', url: '', badge: '', badge_es: '', image: '', featured: false, active: true, order: 0 };

const LinksPanel = () => {
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState(emptyItem);
  const [editingId, setEditingId] = useState(null);
  const [saved, setSaved] = useState('');

  const fetchData = useCallback(async () => {
    const res = await adminFetch(`${API_URL}/api/links/content?include_inactive=true`);
    const data = await res.json();
    setProfile(data.profile);
    setLinks(data.links);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const flash = (msg) => { setSaved(msg); setTimeout(() => setSaved(''), 2500); };

  const saveProfile = async () => {
    const res = await adminFetch(`${API_URL}/api/links/profile`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(profile)
    });
    flash(res.ok ? 'Perfil guardado' : 'Error al guardar');
  };

  const submitItem = async () => {
    const body = { ...form, order: Number(form.order) || 0 };
    const url = editingId ? `${API_URL}/api/links/items/${editingId}` : `${API_URL}/api/links/items`;
    const res = await adminFetch(url, { method: editingId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!res.ok) { flash('Error al guardar el link'); return; }
    setForm(emptyItem); setEditingId(null); fetchData(); flash(editingId ? 'Link actualizado' : 'Link añadido');
  };

  const patchItem = async (item, changes) => {
    const { id, clicks, created_at, ...rest } = item;
    const res = await adminFetch(`${API_URL}/api/links/items/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...rest, ...changes })
    });
    if (!res.ok) flash('Error al actualizar');
    fetchData();
  };

  const move = async (index, direction) => {
    const other = links[index + direction];
    const item = links[index];
    if (!other) return;
    await patchItem(item, { order: other.order });
    await patchItem(other, { order: item.order });
  };

  const removeItem = async (id) => {
    if (!window.confirm('¿Eliminar este link?')) return;
    const res = await adminFetch(`${API_URL}/api/links/items/${id}`, { method: 'DELETE' });
    if (!res.ok) flash('Error al eliminar');
    fetchData();
  };

  const updateOffer = (i, field, value) => {
    const offers = profile.offers.map((o, idx) => (idx === i ? { ...o, [field]: value } : o));
    setProfile({ ...profile, offers });
  };

  if (!profile) return <p className="text-gray-500 text-sm">Cargando...</p>;

  return (
    <div className="space-y-6" data-testid="links-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold">Página /links</h3>
          <p className="text-gray-500 text-xs mt-1">Tu bio-link para redes. Sustituye a Beacons.</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-green-400 text-xs" data-testid="links-saved-msg">{saved}</span>}
          <a href="/links" target="_blank" rel="noopener noreferrer" className="text-xs border border-gray-700 rounded px-3 py-2 hover:border-gray-400 flex items-center gap-2" data-testid="links-preview-btn">
            <ExternalLink size={13} /> Ver página
          </a>
        </div>
      </div>

      {/* Profile */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-4">
        <h4 className="font-semibold text-sm">Cabecera</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className={label}>Nombre</label><input className={input} value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} data-testid="links-name-input" /></div>
          <div><label className={label}>Foto / logo (URL)</label><input className={input} value={profile.avatar} onChange={e => setProfile({ ...profile, avatar: e.target.value })} /></div>
          <div><label className={label}>Titular (EN)</label><input className={input} value={profile.headline} onChange={e => setProfile({ ...profile, headline: e.target.value })} /></div>
          <div><label className={label}>Titular (ES)</label><input className={input} value={profile.headline_es} onChange={e => setProfile({ ...profile, headline_es: e.target.value })} /></div>
          <div><label className={label}>Bio (EN)</label><textarea rows={2} className={input} value={profile.bio} onChange={e => setProfile({ ...profile, bio: e.target.value })} /></div>
          <div><label className={label}>Bio (ES)</label><textarea rows={2} className={input} value={profile.bio_es} onChange={e => setProfile({ ...profile, bio_es: e.target.value })} /></div>
          <div><label className={label}>Instagram</label><input className={input} value={profile.instagram} onChange={e => setProfile({ ...profile, instagram: e.target.value })} /></div>
          <div><label className={label}>LinkedIn</label><input className={input} value={profile.linkedin} onChange={e => setProfile({ ...profile, linkedin: e.target.value })} /></div>
          <div><label className={label}>WhatsApp (link wa.me)</label><input className={input} value={profile.whatsapp} onChange={e => setProfile({ ...profile, whatsapp: e.target.value })} /></div>
          <div><label className={label}>Email</label><input className={input} value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} /></div>
        </div>

        <h4 className="font-semibold text-sm pt-2">Tarjetas de servicios (con botón Web y Presentación)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className={label}>Título sección (EN)</label><input className={input} value={profile.offers_title} onChange={e => setProfile({ ...profile, offers_title: e.target.value })} /></div>
          <div><label className={label}>Título sección (ES)</label><input className={input} value={profile.offers_title_es} onChange={e => setProfile({ ...profile, offers_title_es: e.target.value })} /></div>
        </div>
        {profile.offers.map((offer, i) => (
          <div key={i} className="border border-gray-800 rounded p-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`links-offer-edit-${i}`}>
            <div><label className={label}>Dato destacado (EN)</label><input className={input} value={offer.metric} onChange={e => updateOffer(i, 'metric', e.target.value)} /></div>
            <div><label className={label}>Dato destacado (ES)</label><input className={input} value={offer.metric_es || ''} onChange={e => updateOffer(i, 'metric_es', e.target.value)} /></div>
            <div><label className={label}>Título (EN)</label><input className={input} value={offer.title} onChange={e => updateOffer(i, 'title', e.target.value)} /></div>
            <div><label className={label}>Título (ES)</label><input className={input} value={offer.title_es} onChange={e => updateOffer(i, 'title_es', e.target.value)} /></div>
            <div><label className={label}>Texto (EN)</label><textarea rows={2} className={input} value={offer.desc} onChange={e => updateOffer(i, 'desc', e.target.value)} /></div>
            <div><label className={label}>Texto (ES)</label><textarea rows={2} className={input} value={offer.desc_es} onChange={e => updateOffer(i, 'desc_es', e.target.value)} /></div>
            <div><label className={label}>Botón WEB (ruta o URL)</label><input className={input} placeholder="/real-estate" value={offer.web_url || ''} onChange={e => updateOffer(i, 'web_url', e.target.value)} data-testid={`links-offer-web-${i}`} /></div>
            <div><label className={label}>Botón PRESENTACIÓN (ruta o URL)</label><input className={input} placeholder="/invest" value={offer.deck_url || ''} onChange={e => updateOffer(i, 'deck_url', e.target.value)} data-testid={`links-offer-deck-${i}`} /></div>
          </div>
        ))}
        <div className="flex gap-3">
          <button onClick={() => setProfile({ ...profile, offers: [...profile.offers, { title: '', title_es: '', desc: '', desc_es: '', metric: '', metric_es: '', web_url: '', deck_url: '' }] })}
            className="text-xs border border-gray-700 rounded px-3 py-2 hover:border-gray-400 flex items-center gap-2" data-testid="links-add-offer-btn">
            <Plus size={13} /> Añadir tarjeta
          </button>
          {profile.offers.length > 0 && (
            <button onClick={() => setProfile({ ...profile, offers: profile.offers.slice(0, -1) })}
              className="text-xs border border-gray-700 rounded px-3 py-2 hover:border-red-500 text-gray-400">
              Quitar última
            </button>
          )}
          <button onClick={saveProfile} className="ml-auto bg-cyan-500 text-black font-bold rounded px-4 py-2 text-sm flex items-center gap-2" data-testid="links-save-profile-btn">
            <Save size={14} /> Guardar cabecera
          </button>
        </div>
      </div>

      {/* Add / edit link */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
        <h4 className="font-semibold text-sm">{editingId ? 'Editar link extra' : 'Añadir link extra'}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className={label}>Título (EN) *</label><input className={input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="link-title-input" /></div>
          <div><label className={label}>Título (ES)</label><input className={input} value={form.title_es} onChange={e => setForm({ ...form, title_es: e.target.value })} /></div>
          <div><label className={label}>Descripción (EN)</label><input className={input} value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} /></div>
          <div><label className={label}>Descripción (ES)</label><input className={input} value={form.subtitle_es} onChange={e => setForm({ ...form, subtitle_es: e.target.value })} /></div>
          <div><label className={label}>URL o ruta interna *</label><input className={input} placeholder="/invest o https://..." value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} data-testid="link-url-input" /></div>
          <div><label className={label}>Etiqueta EN (opcional)</label><input className={input} placeholder="20-40%" value={form.badge} onChange={e => setForm({ ...form, badge: e.target.value })} /></div>
          <div><label className={label}>Etiqueta ES (opcional)</label><input className={input} placeholder="20-40%" value={form.badge_es} onChange={e => setForm({ ...form, badge_es: e.target.value })} /></div>
          <div><label className={label}>Imagen (opcional)</label><input className={input} value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} /></div>
          <div><label className={label}>Orden</label><input type="number" className={input} value={form.order} onChange={e => setForm({ ...form, order: e.target.value })} /></div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} /> Destacado</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} /> Activo</label>
          <button onClick={submitItem} disabled={!form.title || !form.url}
            className="ml-auto bg-cyan-500 text-black font-bold rounded px-4 py-2 text-sm disabled:opacity-40 flex items-center gap-2" data-testid="link-submit-btn">
            <Plus size={14} /> {editingId ? 'Guardar cambios' : 'Añadir'}
          </button>
          {editingId && <button onClick={() => { setForm(emptyItem); setEditingId(null); }} className="text-xs text-gray-400 underline">Cancelar</button>}
        </div>
      </div>

      {/* List */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg divide-y divide-gray-800" data-testid="links-admin-list">
        {links.map((item, i) => (
          <div key={item.id} className={`p-3 flex items-center gap-3 ${item.active ? '' : 'opacity-50'}`} data-testid={`links-admin-item-${i}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-gray-500 hover:text-white disabled:opacity-30" data-testid={`links-up-${i}`}><ChevronUp size={14} /></button>
              <button onClick={() => move(i, 1)} disabled={i === links.length - 1} className="text-gray-500 hover:text-white disabled:opacity-30" data-testid={`links-down-${i}`}><ChevronDown size={14} /></button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate flex items-center gap-2">
                {item.featured && <Star size={12} style={{ color: '#d6b36a' }} />}
                {item.title}
              </p>
              <p className="text-gray-500 text-xs truncate">{item.url} · {item.clicks || 0} clicks</p>
            </div>
            <button onClick={() => patchItem(item, { active: !item.active })} className="text-gray-400 hover:text-white" title="Activar/ocultar" data-testid={`links-toggle-${i}`}>
              {item.active ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
            <button onClick={() => { const { id, clicks, created_at, ...rest } = item; setForm(rest); setEditingId(id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="text-xs border border-gray-700 rounded px-2 py-1 hover:border-gray-400" data-testid={`links-edit-${i}`}>Editar</button>
            <button onClick={() => removeItem(item.id)} className="text-red-500 hover:text-red-400" data-testid={`links-delete-${i}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {links.length === 0 && <p className="p-4 text-gray-500 text-sm">Aún no hay links.</p>}
      </div>
    </div>
  );
};

export default LinksPanel;
